import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { Fragment, useState, useRef } from 'react';

import { platformEntity, entityAction, communicationStatus } from '~constants';
import * as landlordActions from '~actions/landlord';
import NewTabs from 'common/components/tabs';
import Header from '~pages/dashboard/landlord/header';
import LandlordModal from '~pages/dashboard/landlord/modal';
import PendingVerification from '~pages/dashboard/landlord/pending-verification';
import AllLandlordTab from '~pages/dashboard/landlord/all';
import authControl from '~components/auth-control';
import useLandlords, { PENDING_TAB, ALL_TAB } from './useLandlords';

const LandlordManagement = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    currentTab,
    filters,
    landlords,
    landlordNameList,
    queryLandlordsCalled,
    queryLandlordsLoading,
    fetchMoreLandlords,
    refetchLandlords,
    handleToggleTab,
    handleChangeFilters,
    pendingPageNumber,
    setPendingPageNumber,
    pendingTotal,
  } = useLandlords();

  const { createLandlordStatus, accountOwners, currentRoleSlug } = useSelector(state => ({
    createLandlordStatus: state.dashboard.landlord?.toJS().communication.create.status,
    accountOwners: state.dashboard.landlord?.toJS().accountOwners,
    currentRoleSlug: state.auth.get('auth').currentRoleSlug,
  }));

  const [isShowModal, setIsShowModal] = useState(false);

  const buttonRef = useRef(null);

  const tabsOption = [
    {
      text: t('cms.landlord.all'),
      value: ALL_TAB,
    },
    {
      text: t('cms.landlord.pending'),
      value: PENDING_TAB,
      extra: (
        <span
          style={{
            display: 'inline-block',
            fontSize: 10,
            lineHeight: '16px',
            padding: '0 6px',
            backgroundColor: '#38B2A6',
            color: '#fff',
            borderRadius: '10px',
            marginLeft: 8,
            verticalAlign: 'middle',
          }}
        >
          {pendingTotal}
        </span>
      ),
    },
  ];

  const setCurrentLandlord = landlord => {
    dispatch(landlordActions.setCurrentLandlord(landlord));
  };

  const createLandlord = (params, successCallbacks) => {
    dispatch(landlordActions.createLandlord(params, successCallbacks));
  };

  const getAccountOwners = (params, successCallback) => {
    dispatch(landlordActions.getAccountOwners(params, successCallback));
  };

  const handleCloseModal = () => {
    setIsShowModal(false);
  };

  const handleShowModal = () => setIsShowModal(true);

  return (
    <Fragment>
      <NewTabs defaultActiveKey={currentTab} onChange={handleToggleTab} options={tabsOption} />

      <Header
        landlords={landlords}
        currentTab={currentTab}
        billingCountries={filters.billingCountryIds}
        accountManager={filters.accountManager}
        setFilters={handleChangeFilters}
        handleShowModal={handleShowModal}
      />

      <Choose>
        <When condition={currentTab === ALL_TAB}>
          <AllLandlordTab
            filters={filters}
            total={landlords.total}
            landlordNameList={landlordNameList}
            loading={queryLandlordsLoading}
            dataSource={landlords.dataSource}
            setFilters={handleChangeFilters}
            setCurrentLandlord={setCurrentLandlord}
          />
        </When>
        <When condition={currentTab === PENDING_TAB}>
          <PendingVerification
            fetchMore={fetchMoreLandlords}
            landlords={landlords}
            loading={queryLandlordsLoading}
            pendingPageNumber={pendingPageNumber}
            queryLandlordsCalled={queryLandlordsCalled}
            refetchLandlords={refetchLandlords}
            setPendingPageNumber={setPendingPageNumber}
          />
        </When>
      </Choose>

      <If condition={isShowModal}>
        <LandlordModal
          activeModal
          t={t}
          type="create"
          createLandlord={createLandlord}
          confirmLoading={createLandlordStatus === communicationStatus.FETCHING}
          accountOwners={accountOwners}
          getAccountOwners={getAccountOwners}
          currentRoleSlug={currentRoleSlug}
          initLandlords={refetchLandlords}
          handleCloseModal={handleCloseModal}
          handleCreateCallback={() => buttonRef.current.click()}
        />
      </If>

      <button
        ref={buttonRef}
        onClick={() => handleToggleTab(ALL_TAB)}
        style={{ display: 'none' }}
      ></button>
    </Fragment>
  );
};

export default authControl(
  platformEntity.LANDLORDS_LANDLORDS,
  entityAction.READ,
)(LandlordManagement);
